import { Paths, PathValue } from "@mctypes/global.types";
import React, { useEffect } from "react";
import { Button, Flex, Paper, Radio, Stack, Text, Title, Box } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../../components";
import McDropdownNew from "../../../../../../../components/McDropdown/MCDropdownNew";
import { KontrollAvLuftstroemEnum } from "@mcapps/shared/gkReportConstants";
import { IGKKontrollAvLuftStroemResultater, IGKRapport } from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import {
  beregnetGjennomsnittligHastighet,
  beregnetLufthastighetUniformitet,
  beregnetStandardAvvik,
  beregnetVarians,
} from "../../gkGlobalUtils";
const KontrollAvLuftstroem: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKKontrollAvLuftStroemResultater;
}> = ({ data, onUpdateEditData }) => {
  const TESTMETODER: KontrollAvLuftstroemEnum[] = [
    KontrollAvLuftstroemEnum.STREAMER,
    KontrollAvLuftstroemEnum.ROEYKELLERPARTIKKEL,
    KontrollAvLuftstroemEnum.LUFTHASTIGHET,
  ];
  const valgtMetode = data.inndata.valgtMetode;
  useEffect(() => {
    utdataBeregninger();
  }, [
    data.inndata.lufthastighetsMetodeResultater.maaleresultater,
    data.inndata.lufthastighetsMetodeResultater.maaleresultater.length,
  ]);
  const addNewRow = () => {
    const maaleresultaterArray = data.inndata.lufthastighetsMetodeResultater.maaleresultater;
    maaleresultaterArray.push(null);
    onUpdateEditData(
      "testResultater.kontrollAvLuftstroem.inndata.lufthastighetsMetodeResultater.maaleresultater",
      maaleresultaterArray,
    );
    return maaleresultaterArray;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = data.inndata.lufthastighetsMetodeResultater.maaleresultater;
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData(
        "testResultater.kontrollAvLuftstroem.inndata.lufthastighetsMetodeResultater.maaleresultater",
        updatedMaaleresultater,
      );
    }
  };
  const updateMaaleresultater = (resultat, i) => {
    const maaleresultater = [...data.inndata.lufthastighetsMetodeResultater.maaleresultater];
    maaleresultater[i] = resultat;
    onUpdateEditData(
      "testResultater.kontrollAvLuftstroem.inndata.lufthastighetsMetodeResultater.maaleresultater",
      maaleresultater,
    );
  };
  const utdataBeregninger = () => {
    if (data.inndata.valgtMetode === KontrollAvLuftstroemEnum.LUFTHASTIGHET) {
      const gjennomsnittligHastighet = beregnetGjennomsnittligHastighet(
        data.inndata.lufthastighetsMetodeResultater.maaleresultater.map((resultat) => resultat),
        data.inndata.lufthastighetsMetodeResultater.maaleresultater.length,
      );
      let varians;
      if (gjennomsnittligHastighet != null) {
        varians = beregnetVarians(
          data.inndata.lufthastighetsMetodeResultater.maaleresultater.map((resultat) => resultat),
          gjennomsnittligHastighet,
        );
      }
      const standardAvvik = beregnetStandardAvvik(varians);
      const lufthastighetUniformitet = beregnetLufthastighetUniformitet(gjennomsnittligHastighet, standardAvvik);
      const utdata = {
        gjennomsnittligHastighet: gjennomsnittligHastighet,
        varians: varians,
        standardAvvik: standardAvvik,
        lufthastighetUniformitet: lufthastighetUniformitet,
      };
      onUpdateEditData("testResultater.kontrollAvLuftstroem.utdata", utdata);
    }
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Kontroll av luftmønster
        </Title>
        <b>Hvilken metode skal brukes?</b>
        <Box w="25%">
          <McDropdownNew
            currentValue={data.inndata.valgtMetode}
            onChange={(_, item) =>
              onUpdateEditData("testResultater.kontrollAvLuftstroem.inndata.valgtMetode", item as string)
            }
            defaultTitle="Hvilken testmetode skal brukes?"
            options={TESTMETODER}
          />
        </Box>
        {(valgtMetode === KontrollAvLuftstroemEnum.STREAMER ||
          valgtMetode === KontrollAvLuftstroemEnum.ROEYKELLERPARTIKKEL) && (
          <Box mt="lg" style={{ width: "50%" }}>
            <div>
              <McTextAreaNew
                label="Beskrivelse av luftmønster:"
                value={data.inndata.streamerMetodeResultater.beskrivelseAvLuftstroem}
                onChange={(e) =>
                  onUpdateEditData(
                    "testResultater.kontrollAvLuftstroem.inndata.streamerMetodeResultater.beskrivelseAvLuftstroem",
                    e.target.value,
                  )
                }
              />
            </div>
            <div>
              <Stack gap={0}>
                <Radio
                  checked={data.inndata.streamerMetodeResultater.godkjent === true}
                  onChange={() =>
                    onUpdateEditData(
                      "testResultater.kontrollAvLuftstroem.inndata.streamerMetodeResultater.godkjent",
                      true,
                    )
                  }
                  label="Godkjent"
                />
                <Radio
                  checked={data.inndata.streamerMetodeResultater.godkjent === false}
                  onChange={() =>
                    onUpdateEditData(
                      "testResultater.kontrollAvLuftstroem.inndata.streamerMetodeResultater.godkjent",
                      false,
                    )
                  }
                  label="Ikke godkjent"
                />
              </Stack>
            </div>
          </Box>
        )}
        {data.inndata.valgtMetode === KontrollAvLuftstroemEnum.LUFTHASTIGHET && (
          <>
            <Box mt="lg">
              <Stack gap={0}>
                <b>Hvor mange målinger skal utføres? &#40;Minimum 2&#41;:</b>
                <Flex>
                  <Button color="green" onClick={addNewRow}>
                    Legg til rad
                  </Button>
                  <HSpace />
                  <Button
                    variant="danger-filled"
                    onClick={removeLastRow}
                    disabled={data.inndata.lufthastighetsMetodeResultater.maaleresultater.length < 3}
                  >
                    Fjern siste rad
                  </Button>
                </Flex>
              </Stack>
            </Box>
            {data.inndata.lufthastighetsMetodeResultater.maaleresultater.length > 0 && (
              <>
                <Flex direction="column" mt="lg">
                  <b>Fyll inn måleresultater &#40;m/s&#41;:</b>
                  {data.inndata.lufthastighetsMetodeResultater.maaleresultater.map((a, i) => (
                    <McInputNew
                      style={{ marginBottom: "0.25rem", width: "25%" }}
                      key={i}
                      value={a}
                      normalizeNumbers={true}
                      type="text"
                      onBlur={(e) => updateMaaleresultater(Number(e.target.value), i)}
                    />
                  ))}
                </Flex>
                <Flex justify="space-between">
                  <Text mb={0}>
                    Gjennomsnittlig hastighet &#40;m/s&#41; =
                    <b>
                      {" "}
                      {beregnetGjennomsnittligHastighet(
                        data.inndata.lufthastighetsMetodeResultater.maaleresultater.map((resultat) => resultat),
                        data.inndata.lufthastighetsMetodeResultater.maaleresultater.length,
                      )?.toFixed(3) ?? ""}
                    </b>
                  </Text>
                  <Text mb={0}>Formel: &#40;Va = &#40;SUM Vn&#41; / N&#41;</Text>
                </Flex>
                <hr style={{ marginTop: 0 }} />
                <Flex justify="space-between">
                  <Text mb={0}>
                    Varians &#40;-&#41; =
                    <b>
                      {" "}
                      {beregnetVarians(
                        data.inndata.lufthastighetsMetodeResultater.maaleresultater.map((resultat) => resultat),
                        data.utdata.gjennomsnittligHastighet,
                      )?.toFixed(4) ?? ""}
                    </b>
                  </Text>
                  <Text mb={0}>
                    Formel: &#40;VRS = &#40;&#40;SUM Vn<sup>2</sup>&#41; - N * &#40;Va<sup>2</sup>&#41;&#41; / &#40;N -
                    1&#41;&#41;
                  </Text>
                </Flex>
                <hr style={{ marginTop: 0 }} />
                <Flex justify="space-between">
                  <Text mb={0}>
                    Standardavvik &#40;-&#41; =<b> {beregnetStandardAvvik(data.utdata.varians)?.toFixed(4) ?? ""}</b>
                  </Text>
                  <Text mb={0}>
                    Formel: &#40;Sigma = VRS<sup>0,5</sup>&#41;
                  </Text>
                </Flex>
                <hr style={{ marginTop: 0 }} />
                <Flex justify="space-between">
                  <Text mb={0}>
                    Lufthastighetens uniformitet &#40;-&#41; ={" "}
                    <b>
                      {beregnetLufthastighetUniformitet(
                        data.utdata.gjennomsnittligHastighet,
                        data.utdata.standardAvvik,
                      )?.toFixed(2) ?? ""}
                    </b>
                  </Text>
                  <Text mb={0}>Formel: &#40;Uv = &#40;1 - &#40;Sigma / Va&#41;&#41; * 100&#41;</Text>
                </Flex>
                <hr style={{ marginTop: 0 }} />
              </>
            )}
          </>
        )}
        <Box mt="lg" style={{ width: "50%" }}>
          <div>
            <ReportDocxImageContainer docxImageTag="kontrollAvLuftstroem" title="Målepunkter i rommet" />
          </div>
          <McTextAreaNew
            label="Kommentar:"
            value={data.inndata.kommentar}
            onChange={(e) => onUpdateEditData("testResultater.kontrollAvLuftstroem.inndata.kommentar", e.target.value)}
          />
        </Box>
      </Stack>
    </Paper>
  );
};
export default KontrollAvLuftstroem;
